import {
  Button,
  Checkbox,
  FormRow,
  Input,
  Modal,
  Radio,
  Spinner,
  Stack,
  Text
} from '@efast_public/fjd-component-library';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { BehApiRoutes, behClient } from '../api/beh-api';
import {
  KeycloakAssignedGroups,
  KeycloakGroup,
  UserRequestDto
} from '../api/hooks/useInvite';
import { useReadUserInviteConfig } from '../api/hooks/useReadUserInviteConfig';
import { useNotification } from '../shared/notification/useNotification';
import {
  ErrorType,
  userConflictErrorMsg
} from '../shared/utils/Error/ErrorCode';
import { getMandant } from '../shared/utils/mandant';

const SYSTEM_GROUP_CONSTANT: string = 'Administration';

interface CreateAndUpdateMandantCredsModalProps {
  readonly onClose: () => void;
  readonly isOpen: boolean;
}

export function UserAndRoleManagementModal({
  onClose
}: CreateAndUpdateMandantCredsModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUser, notifyUserFromRequest } = useNotification();
  const [email, setEmail] = useState('');

  const [selectedKeycloakGroups, setSelectedKeycloakGroups] = useState<
    Array<KeycloakAssignedGroups>
  >([]);

  const { mutateAsync: inviteUser, isPending: isInviting } = useMutation({
    mutationFn: (userRequest: UserRequestDto) =>
      notifyUserFromRequest(
        () => behClient.post(BehApiRoutes.users(), userRequest),
        {
          successMessage: 'Einladung wurde erfolgreich versendet!',
          errorMessage: 'Einladung konnte nicht versendet werden.',
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_USER_CONFLICT]: () => {
              notifyUser({ intent: 'error', title: userConflictErrorMsg });
            }
          }
        }
      ),
    onSuccess: () => {
      onClose();
      queryClient.invalidateQueries({
        queryKey: [BehApiRoutes.userRoles()]
      });
    }
  });

  const { data: inviteConfig } = useReadUserInviteConfig();

  const assignableGroups: Array<KeycloakAssignedGroups> = Object.entries(
    inviteConfig?.groupRoles ?? {}
  ).map((group) => ({
    groupName: group[0],
    subGroups: group[1] as Array<KeycloakGroup>
  }));

  const onInviteRequest = async (
    emailInput: string,
    groups: Array<KeycloakAssignedGroups>
  ) => {
    await inviteUser({
      email: emailInput,
      groups: groups,
      redirectUri: `${window.location.origin}/${getMandant()}`
    } as UserRequestDto).catch(() => {});
  };

  function isValidEmail(email: string) {
    return /\S+@\S+\.\S+/.test(email);
  }

  function areSelectedRolesValid() {
    const hasSelectedAdminRole: boolean = selectedKeycloakGroups.some(
      (k) => k.groupName === SYSTEM_GROUP_CONSTANT
    );
    const hasNotSelectedSubGroupName: boolean = selectedKeycloakGroups.some(
      (k) =>
        k.groupName &&
        k.groupName !== SYSTEM_GROUP_CONSTANT &&
        k.subGroups.length === 0
    );
    if (hasNotSelectedSubGroupName) {
      return false;
    }
    if (hasSelectedAdminRole) {
      return true;
    }
    return selectedKeycloakGroups.length > 0;
  }

  const isInputValid = () => {
    if (email === '') {
      return false;
    }

    if (!isValidEmail(email)) {
      return false;
    }
    return areSelectedRolesValid();
  };

  const checkedRadio = (groupName: string, groupIds: Array<string>) => {
    const assign = selectedKeycloakGroups.find(
      (value) => value.groupName === groupName
    );

    if (!assign) {
      return false;
    }

    if (assign.subGroups.length !== groupIds.length) {
      return false;
    }

    let correct = true;

    for (const subgroup of assign.subGroups) {
      let innerCorrect = false;

      if (groupIds.some((id) => id === subgroup.id)) {
        innerCorrect = true;
      }

      correct = correct && innerCorrect;
    }
    return correct;
  };

  const checkRadioDisabled = (groupName: string) => {
    return (
      selectedKeycloakGroups.find((value) => value.groupName === groupName) ===
      undefined
    );
  };

  const onRadioChanged = (
    checked: boolean,
    groupName: string,
    subgroups: Array<KeycloakGroup>
  ) => {
    if (checked) {
      setSelectedKeycloakGroups((prev) =>
        prev.map((value) =>
          value.groupName === groupName
            ? ({
                groupName: value.groupName,
                subGroups: subgroups
              } as KeycloakAssignedGroups)
            : value
        )
      );
    }
  };

  const onSystemRoleChange = (checked: boolean, groupName: string) => {
    setSelectedKeycloakGroups((prev) => {
      if (checked) {
        return [
          ...prev,
          {
            groupName: groupName,
            subGroups: []
          } as KeycloakAssignedGroups
        ];
      } else {
        return prev.filter((value) => value.groupName !== groupName);
      }
    });
  };

  const onChangeCheckbox = (checked: boolean, groupName: string) => {
    if (checked) {
      setSelectedKeycloakGroups((prev) => [
        ...prev,
        { groupName: groupName, subGroups: [] } as KeycloakAssignedGroups
      ]);
    } else {
      setSelectedKeycloakGroups((prev) =>
        prev.filter((value) => value.groupName !== groupName)
      );
    }
  };
  return (
    <Modal open onClose={onClose}>
      <Modal.Header id="UserAndRoleManagementModal-header">
        {t('userAndroleManagementModal.headline')}
      </Modal.Header>

      <Modal.Content>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Stack spacing="l">
            <FormRow label={t('userAndroleManagementModal.emailInput')}>
              <Input
                id="emailToRegister"
                type="text"
                onChange={(event) => setEmail(event.target.value)}
                value={email}
                placeholder="example@abc.com"
                disabled={isInviting}
                pattern={/\S+@\S+\.\S+/.source}
              />
            </FormRow>
            <FormRow label="Rollen nach Gruppen">
              <Stack
                id="groupRowStack"
                className="stackLeft"
                direction="column"
                spacing="s"
              >
                {assignableGroups.map((value: KeycloakAssignedGroups) => {
                  return (
                    <Stack
                      className="role-container"
                      direction="column"
                      spacing="xs"
                    >
                      <Checkbox
                        key={`${value.groupName}_checkbox`}
                        label={`${value.groupName}:`}
                        id={`group_${value.groupName}`}
                        onChange={(event) => {
                          onChangeCheckbox(
                            event.currentTarget.checked,
                            value.groupName
                          );
                        }}
                      />
                      {!checkRadioDisabled(value.groupName) && (
                        <Stack
                          direction="row"
                          spacing="s"
                          className="userRoles-radioButtons"
                          style={{ marginBottom: 'var(--fjd2-spacing-s)' }}
                        >
                          <Text>{t('userAndroleManagementModal.role')}</Text>
                          {value.subGroups
                            ? value.subGroups
                                .sort((a, b) => a.name.localeCompare(b.name))
                                .map((subGroup: KeycloakGroup) => {
                                  const keycloakSubGroups = value.subGroups;
                                  return (
                                    <Radio
                                      key={subGroup.id}
                                      disabled={checkRadioDisabled(
                                        value.groupName
                                      )}
                                      value={subGroup.id}
                                      label={subGroup.name}
                                      id={`subgroupRadio_${subGroup.id}`}
                                      checked={checkedRadio(
                                        value.groupName,
                                        subGroup.name === 'Teamleitung'
                                          ? keycloakSubGroups.map(
                                              (role) => role.id
                                            )
                                          : [subGroup.id]
                                      )}
                                      name={value.groupName}
                                      onChange={(event) => {
                                        onRadioChanged(
                                          event.currentTarget.checked,
                                          value.groupName,
                                          subGroup.name === 'Teamleitung'
                                            ? keycloakSubGroups
                                            : [subGroup]
                                        );
                                      }}
                                    />
                                  );
                                })
                            : ''}
                        </Stack>
                      )}
                    </Stack>
                  );
                })}
              </Stack>
            </FormRow>
            <FormRow
              label={t('userAndroleManagementModal.adminLabel')}
              tooltip={t('userAndroleManagementModal.adminTooltip')}
            >
              <Stack id="systemGroupStack" direction="column">
                {inviteConfig?.systemRoles.map((value: KeycloakGroup) => (
                  <Checkbox
                    label={value.name}
                    id={`rolle_${value.name}`}
                    key={`key_rolle_${value.name}`}
                    onChange={(event) =>
                      onSystemRoleChange(
                        event.currentTarget.checked,
                        value.name
                      )
                    }
                  />
                ))}
              </Stack>
            </FormRow>
          </Stack>
        </form>
      </Modal.Content>
      <Modal.Footer direction="rtl" alignment="none">
        <Button variant="secondary-outline" onClick={onClose}>
          {t('globalNotifications.modalAboard')}
        </Button>
        <Button
          id="submitButton"
          variant="primary"
          onClick={() => {
            onInviteRequest(email, selectedKeycloakGroups);
          }}
          disabled={!isInputValid() || isInviting}
        >
          {isInviting ? <Spinner size="sm" /> : 'Einladung versenden'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
